/**
 * Song Requests - the module's own API (R25 S8), at /api/m/song_requests/.
 *
 * Every route carries the channel id; the platform's guard admits the
 * channel's owner, a ChannelPermission holder or a super admin. Settings are
 * NOT here: the settings page stays on core's settings API (layering, locks,
 * invalidation).
 */

import api from '@/api/client'

const BASE = '/api/m/song_requests'

export interface SongInfo {
  title: string
  artist: string | null
  id: string | null
  url: string | null
  duration_seconds: number | null
  thumbnail_url: string | null
}

export interface QueueItem {
  id: number
  requester: { twitch_id: string; username: string }
  song: SongInfo
  status: string
  position: number | null
  is_priority: boolean
  created_at: string
}

export interface PlayerStatus {
  connected: boolean
  /** Why not connected: never_pushed | stale | player_offline */
  reason: string | null
  state: 'playing' | 'paused' | 'buffering' | 'unknown' | null
  title: string | null
  artist: string | null
  video_id: string | null
  duration_seconds: number | null
  progress_seconds: number | null
  ad_playing: boolean
  observed_at: string | null
  /** Set when the track playing is a request. */
  request_id: number | null
  requested_by: string | null
}

export interface SongRequestStatus {
  player: PlayerStatus
  queue: QueueItem[]
  queue_length: number
}

export type BanType = 'song' | 'artist' | 'requester'

export interface Ban {
  id: number
  ban_type: BanType
  value: string
  label: string | null
  reason: string | null
  banned_by: string
  created_at: string
}

export type MoveWhere = 'front' | 'up' | 'down'

export const songRequestsApi = {
  status: (channelId: number) =>
    api.get<SongRequestStatus>(`${BASE}/status/${channelId}`),

  history: (channelId: number, limit = 20) =>
    api.get<QueueItem[]>(`${BASE}/history/${channelId}?limit=${limit}`),

  cancel: (channelId: number, requestId: number) =>
    api.post<QueueItem>(`${BASE}/queue/${channelId}/${requestId}/cancel`),

  move: (channelId: number, requestId: number, where: MoveWhere) =>
    api.post<QueueItem[]>(`${BASE}/queue/${channelId}/${requestId}/move`, { where }),

  clear: (channelId: number) =>
    api.post<{ cleared: number }>(`${BASE}/queue/${channelId}/clear`),

  skip: (channelId: number) =>
    api.post<{ result: 'next_request' | 'ytmd_next'; started: QueueItem | null }>(
      `${BASE}/skip/${channelId}`,
    ),

  bans: (channelId: number) => api.get<Ban[]>(`${BASE}/bans/${channelId}`),

  addBan: (channelId: number, ban: { ban_type: BanType; value: string; reason?: string }) =>
    api.post<Ban>(`${BASE}/bans/${channelId}`, ban),

  removeBan: (channelId: number, banId: number) =>
    api.delete<void>(`${BASE}/bans/${channelId}/${banId}`),
}

/** Seconds as m:ss (or h:mm:ss). */
export function mmss(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '–:––'
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = (s % 60).toString().padStart(2, '0')
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${r}` : `${m}:${r}`
}

/** A reader-facing line for why the player is not connected. */
export function playerOfflineText(reason: string | null): string {
  switch (reason) {
    case 'player_offline':
      return "YTM Desktop isn't reachable"
    case 'stale':
      return 'No word from YTM Desktop for over 90 seconds'
    default:
      return 'Not watching a player on this channel - check the connection settings'
  }
}
